import { useTranslation } from "react-i18next";
import styled from "styled-components";
import BookingSummaryItem from "./BookingSummaryItem/BookingSummaryItem";
import { BookingSummaryStyles } from "../../types/styles";
import {
  CalendarSolid,
  ClockSolid,
  ClockOutline,
  UserSolid,
} from "../../assets/icons";
import VenueInfo from "../VenueInfo/VenueInfo";

const Container = styled.div<{
  styles: BookingSummaryStyles;
  flashBookingSummaryPanel?: boolean;
}>`
  width: 100%;
  background-color: ${({ styles }) => styles.BookingSummaryBackgroundColour};
  border-radius: ${({ styles }) => styles.BookingSummaryBorderRadius}px;
  color: ${({ styles }) => styles.BookingSummaryTextColour};
  padding: 4px 0;
  display: flex;
  flex-direction: column;
  row-gap: 4px;

  ${({ flashBookingSummaryPanel }) =>
    (flashBookingSummaryPanel &&
      `
      animation: flash 1s infinite;
      @keyframes flash {
        0% {
          opacity: 100%;
        }
        50% {
          opacity: 0%;
        }
        100% {
          opacity: 100%;
        }
      }
    `) ||
    ""}
`;

const StyledDiv = styled.div`
  display: grid;
  width: fit-content;
  grid-template-columns: repeat(3, auto);
  column-gap: 24px;
  text-align: center;
`;

export type BookingSummaryProps = {
  numberOfCovers: number;
  date: Date;
  timeslot?: string | null;
  styles: BookingSummaryStyles;
  /**
   * Should be passed in if user has selected a cross-sell venue.
   */
  newProvider?: {
    name: string;
    location: string;
    locationUrl: string;
  };
  flashBookingSummaryPanel?: boolean;
  isErrored?: boolean;
};

export const BookingSummary: React.FC<BookingSummaryProps> = ({
  numberOfCovers,
  date,
  timeslot,
  styles,
  newProvider,
  flashBookingSummaryPanel,
  isErrored,
}) => {
  const { t } = useTranslation();

  return (
    <Container
      styles={styles}
      flashBookingSummaryPanel={flashBookingSummaryPanel}
      data-id="booking-summary"
    >
      {newProvider && (
        <VenueInfo
          providerName={newProvider.name}
          providerLocation={newProvider.location}
          providerLocationUrl={newProvider.locationUrl}
          styles={{
            ...styles,
            OverallGeneralTextColour: styles.BookingSummaryTextColour,
          }}
        />
      )}
      <StyledDiv>
        <BookingSummaryItem
          icon={UserSolid}
          styles={styles}
          dataId="booking-summary-covers"
        >
          {numberOfCovers.toString()}
        </BookingSummaryItem>
        <BookingSummaryItem
          icon={CalendarSolid}
          styles={styles}
          dataId="booking-summary-date"
        >
          {new Date(date).toLocaleString("default", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          })}
        </BookingSummaryItem>
        <BookingSummaryItem
          icon={timeslot ? ClockSolid : ClockOutline}
          styles={styles}
          isErrored={isErrored && !timeslot}
          dataId="booking-summary-time-slot"
        >
          {timeslot ? timeslot : t("SelectTimeSlot")}
        </BookingSummaryItem>
      </StyledDiv>
    </Container>
  );
};

export default BookingSummary;
